import type { ReactNode } from 'react'
import { useRouter } from '@tanstack/react-router'
import { clerkConfig } from '@/config/clerk'
import { ClerkProvider as BaseClerkProvider } from '@clerk/clerk-react'
import { UserProvider } from './user-context'
import { ApiConfigProvider } from './api-config'

interface ClerkContextProviderProps {
  children: ReactNode
}

/**
 * Extended ClerkProvider with application-specific configuration
 */
export function ClerkContextProvider({ children }: ClerkContextProviderProps) {
  const router = useRouter()

  // Safe navigation functions that check if router is available
  const routerPush = (to: string) => {
    if (router) {
      void router.navigate({ to })
    } else {
      // Fallback to window.location if router is not available
      window.location.href = to
    }
  }

  const routerReplace = (to: string) => {
    if (router) {
      void router.navigate({ to, replace: true })
    } else {
      // Fallback to window.location if router is not available
      window.location.replace(to)
    }
  }

  return (
    <BaseClerkProvider
      publishableKey={clerkConfig.publishableKey}
      routerPush={routerPush}
      routerReplace={routerReplace}
      // Updated to use signInFallbackRedirectUrl and signUpFallbackRedirectUrl as per Clerk v5.30.2+ docs: https://clerk.com/docs/guides/custom-redirects
      signInFallbackRedirectUrl={clerkConfig.routes.afterSignIn}
      signUpFallbackRedirectUrl={clerkConfig.routes.afterSignUp}
      signInUrl={clerkConfig.routes.signIn}
      signUpUrl={clerkConfig.routes.signUp}
    >
      <UserProvider>
        <ApiConfigProvider>
          {children}
        </ApiConfigProvider>
      </UserProvider>
    </BaseClerkProvider>
  )
}

// Hooks are now exported from @/hooks/use-clerk for better Fast Refresh compatibility
export {
  useIsAuthenticated,
  useAuthUser,
  useAuthOrganizations,
} from '@/hooks/use-clerk'
